import { useState, useRef } from "react";
import { Image } from "@tiptap/extension-image";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";

// Handle positions: t=top, b=bottom, l=left, r=right, c=center
type HandlePos = "tl" | "tc" | "tr" | "ml" | "mr" | "bl" | "bc" | "br";

const HANDLES: { pos: HandlePos; cursor: string; top?: number | string; bottom?: number | string; left?: number | string; right?: number | string; transform?: string }[] = [
  { pos: "tl", cursor: "nw-resize",  top: -5,    left: -5 },
  { pos: "tc", cursor: "n-resize",   top: -5,    left: "50%", transform: "translateX(-50%)" },
  { pos: "tr", cursor: "ne-resize",  top: -5,    right: -5 },
  { pos: "ml", cursor: "w-resize",   top: "50%", left: -5,   transform: "translateY(-50%)" },
  { pos: "mr", cursor: "e-resize",   top: "50%", right: -5,  transform: "translateY(-50%)" },
  { pos: "bl", cursor: "sw-resize",  bottom: -5, left: -5 },
  { pos: "bc", cursor: "s-resize",   bottom: -5, left: "50%", transform: "translateX(-50%)" },
  { pos: "br", cursor: "se-resize",  bottom: -5, right: -5 },
];

const ResizableImageView = ({ node, updateAttributes, selected }: NodeViewProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const handleMouseDown = (e: React.MouseEvent, pos: HandlePos) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth  = Number(node.attrs.width  ?? imgRef.current?.offsetWidth  ?? imgRef.current?.naturalWidth  ?? 300);
    const startHeight = Number(node.attrs.height ?? imgRef.current?.offsetHeight ?? imgRef.current?.naturalHeight ?? 200);

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const attrs: Record<string, number> = {};

      // Width: right-side handles grow right (+dx), left-side handles grow left (-dx)
      if (pos.includes("r")) attrs.width  = Math.max(40, Math.round(startWidth  + dx));
      if (pos.includes("l")) attrs.width  = Math.max(40, Math.round(startWidth  - dx));
      // Height: bottom handles grow down (+dy), top handles grow up (-dy)
      if (pos.includes("b")) attrs.height = Math.max(20, Math.round(startHeight + dy));
      if (pos.includes("t")) attrs.height = Math.max(20, Math.round(startHeight - dy));

      // Lock the other dimension explicitly so the browser doesn't auto-scale aspect ratio
      if ("height" in attrs && !("width" in attrs)) attrs.width  = startWidth;
      if ("width"  in attrs && !("height" in attrs)) attrs.height = startHeight;

      if (Object.keys(attrs).length) updateAttributes(attrs);
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const showHandles = selected || isHovered;

  return (
    <NodeViewWrapper
      as="span"
      style={{ display: "inline-block", position: "relative", lineHeight: 0, userSelect: "none" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <img
        ref={imgRef}
        src={node.attrs.src}
        alt={node.attrs.alt ?? ""}
        style={{
          width:  node.attrs.width  ? `${node.attrs.width}px`  : undefined,
          height: node.attrs.height ? `${node.attrs.height}px` : undefined,
          display: "block",
          outline: showHandles ? "1px solid #0078d4" : "none",
          cursor: "default",
        }}
        draggable={false}
      />
      {showHandles && HANDLES.map(({ pos, cursor, top, bottom, left, right, transform }) => (
        <div
          key={pos}
          onMouseDown={(e) => handleMouseDown(e, pos)}
          style={{
            position: "absolute",
            top, bottom, left, right, transform,
            width: 10,
            height: 10,
            background: "#ffffff",
            border: "1px solid #0078d4",
            borderRadius: "50%",
            cursor,
            zIndex: 10,
            boxSizing: "border-box",
          }}
        />
      ))}
    </NodeViewWrapper>
  );
};

export const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) =>
          el.getAttribute("width") ||
          (el as HTMLImageElement).style.width?.replace("px", "") ||
          null,
        renderHTML: (attrs) => {
          if (!attrs.width && !attrs.height) return {};
          const styleParts: string[] = [];
          if (attrs.width)  styleParts.push(`width:${attrs.width}px`);
          if (attrs.height) styleParts.push(`height:${attrs.height}px`);
          const result: Record<string, string> = { style: styleParts.join(";") };
          if (attrs.width) result.width = String(attrs.width);
          return result;
        },
      },
      height: {
        default: null,
        parseHTML: (el) =>
          el.getAttribute("height") ||
          (el as HTMLImageElement).style.height?.replace("px", "") ||
          null,
        renderHTML: () => ({}), // Handled inside width's renderHTML above
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});
